import {
  ErrorDisplay,
  FormHub,
  LoadingIndicator,
  ResponsiveContainer,
  Section,
  useFormEvents,
  useObservable,
} from '@lib/common';
import {
  GuestSurveyForms,
  guestSurveyQuery,
  guestSurveyService,
  propertyQuery,
  propertyService,
} from '@lib/state';
import { Grid, Typography } from '@material-ui/core';
import React, { useCallback, useEffect, useMemo } from 'react';

import { RouteComponentProps } from 'react-router-dom';

import {
  StartGuestSurveyRequest,
  SubmitGuestSurveyRequest,
} from '@lib/state/api/generated/properties';
import { ReservationInfo, GuestSurveyForm, GuestSurveyFormFields } from '..';

export const GuestSurveyPage: React.FC<RouteComponentProps<{
  propertyId: string;
  userId: string;
  reservationId: string;
  overallRating: string;
}>> = ({ match }) => {
  const propertyId = match.params.propertyId;
  const userId = match.params.userId;
  const reservationId = match.params.reservationId;

  const guestSurveyFromStore = useObservable(guestSurveyQuery.guestSurvey);
  const properties = useObservable(propertyQuery.properties);

  const [{ error: startErrorStatus }] = useFormEvents(GuestSurveyForms.StartGuestSurvey);

  const [{ status: submitFormStatus, error: submitErrorStatus }] = useFormEvents(
    GuestSurveyForms.SubmitGuestSurvey
  );

  const startGuestSurveyRequest: StartGuestSurveyRequest = useMemo(() => {
    return { userId, reservationId };
  }, [userId, reservationId]);

  const onSubmit = useCallback(
    (fields: GuestSurveyFormFields) => {
      const submitGuestSurveyRequest: SubmitGuestSurveyRequest = {
        facilitiesRating: fields.facilitiesRating,
        staffRating: fields.staffRating,
        foodRating: fields.foodRating,
        comments: fields.comments,
        userId: userId,
        reservationId: reservationId,
        makeTicket: fields.makeTicket,
      };
      guestSurveyService.submitGuestSurvey(propertyId, submitGuestSurveyRequest);
    },
    [propertyId, userId, reservationId]
  );

  const property = useMemo(() => {
    return properties.find(f => f.id === propertyId);
  }, [properties, propertyId]);

  useEffect(() => {
    if (!!userId) guestSurveyService.startGuestSurvey(propertyId, startGuestSurveyRequest);
  }, [userId, propertyId, startGuestSurveyRequest]);

  useEffect(() => {
    propertyService.loadProperties();
  }, []);

  if (!guestSurveyFromStore && !startErrorStatus)
    return <LoadingIndicator loadingText="Loading" fillContainer />;

  return (
    <ResponsiveContainer disableGutters>
      <Section title={'Guest Survey'} maxWidth="md" variant="contained">
        {startErrorStatus ? (
          <Typography variant="h5">
            <ErrorDisplay error={startErrorStatus}></ErrorDisplay>
          </Typography>
        ) : (
          <>
            {guestSurveyFromStore && (
              <Grid style={{ paddingBottom: '1em' }}>
                <ReservationInfo survey={guestSurveyFromStore} property={property} />
              </Grid>
            )}
            <FormHub onSubmit={onSubmit}>
              <GuestSurveyForm status={submitFormStatus} error={submitErrorStatus} />
            </FormHub>
          </>
        )}
      </Section>
    </ResponsiveContainer>
  );
};
